import { useEffect, useId, useRef, useState } from 'react'
import { filterOptions, toggleValue, type PickOption } from '../lib/multiPick'
import { Icon } from './icons'
import { useStaffText } from './StaffText'

export type { PickOption } from '../lib/multiPick'

/**
 * A searchable multi-select: chosen items as removable chips, a dashed trigger,
 * and a panel with a search box and the options. Closes on Escape and on a click
 * outside; the selection is kept.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  label,
  disabled = false,
}: {
  options: readonly PickOption[]
  value: readonly string[]
  onChange: (value: string[]) => void
  /** The trigger's words, e.g. "Select queues". */
  placeholder?: string
  searchPlaceholder?: string
  /** The trigger's accessible name, when several pickers share one placeholder. */
  label?: string
  disabled?: boolean
}) {
  const { say } = useStaffText()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const root = useRef<HTMLDivElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const chosen = value.map((v) => options.find((o) => o.value === v)).filter((o): o is PickOption => o !== undefined)
  const shown = filterOptions(options, query)

  return (
    <div className="multi" ref={root}>
      {chosen.length > 0 && (
        <div className="multi__chips">
          {chosen.map((o) => (
            <span key={o.value} className="multi__chip">
              {o.label}
              <button
                type="button"
                disabled={disabled}
                aria-label={say('ui.removeItem', { name: o.label })}
                onClick={() => onChange(value.filter((v) => v !== o.value))}
              >
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <button
        type="button"
        className="multi__trigger"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="plus" size={16} />
        <span className="multi__trigger-text">{placeholder ?? say('ui.select')}</span>
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="multi__pop">
          <input
            className="multi__search"
            autoFocus
            value={query}
            placeholder={searchPlaceholder ?? say('ui.search')}
            aria-label={searchPlaceholder ?? say('ui.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="multi__options" id={listId} role="listbox" aria-multiselectable="true">
            {shown.length === 0 && <div className="multi__empty">{say('ui.noMatches')}</div>}
            {shown.map((o) => {
              const on = value.includes(o.value)
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  className="multi__option"
                  aria-selected={on}
                  disabled={o.disabled && !on}
                  onClick={() => onChange(toggleValue(value, o))}
                >
                  <span className="multi__option-text">
                    <b>{o.label}</b>
                    {o.note && <small>{o.note}</small>}
                  </span>
                  <span className="multi__tick">
                    <Icon name="check" size={16} />
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
